import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { EventEmitter, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, tap } from 'rxjs/operators';

import { OdvDocumentsService } from '@shared/services/api/odv-documents.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { OdvDocuments } from '@shared/entities/odvDocuments';
import { ThemeService } from '../../../services/theme.service';

@Component({
  selector: 'app-odv-history',
  templateUrl: './odv-history.component.html',
  styleUrl: './odv-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OdvHistoryComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private odvDocumentsService = inject(OdvDocumentsService);
  private fsAttachmentService = inject(FsAttachmentService);
  private _themeService = inject(ThemeService);

  @Input() salesOrderDocumentId: string | null = null;
  @Input() uploadablePeriodically: boolean | null = null;
  /**
   * Whether the current user may clear an uploaded file. It mirrors who may upload: the page
   * passes its own canShowFileInput(), so clearing and uploading are allowed to the same people.
   */
  @Input() canClear = false;

  /** How many files are marked for removal, so the page can enable its save button. */
  @Output() pendingRemovalsChange = new EventEmitter<number>();

  odvHistory$: Observable<OdvDocuments[]> = of([]);
  isLoading = false;
  isDownloading = false;
  isLight = true;

  /** Ids marked for removal: nothing is deleted until the page saves. */
  private readonly markedForRemoval = new Set<string>();

  constructor() {
    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (this.salesOrderDocumentId) {
      this.loadOdvHistory();
    }
  }

  private loadOdvHistory(onLoaded?: () => void): void {
    if (!this.salesOrderDocumentId) return;

    this.isLoading = true;
    this._cdr.markForCheck();

    this.odvDocumentsService
      .getBySalesOrderDocumentId(this.salesOrderDocumentId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        map((result) => {
          this.isLoading = false;

          const data = result?.data || [];

          return data.sort((a, b) => {
            const dateA = new Date(a.createdDate || '').getTime();
            const dateB = new Date(b.createdDate || '').getTime();
            return dateB - dateA;
          });
        }),
        catchError((error) => {
          console.error('Error loading ODV history:', error);
          this.isLoading = false;
          this._cdr.markForCheck();
          return of([]);
        })
      )
      .subscribe((data) => {
        this.odvHistory$ = of(data);
        this._cdr.markForCheck();
        onLoaded?.();
      });
  }

  downloadDocument(documentId: string, fileName?: string): void {
    if (!documentId || this.isDownloading) {
      return;
    }

    this.isDownloading = true;
    this._cdr.markForCheck();

    this.fsAttachmentService
      .downloadFile(documentId, fileName || 'document')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this.isDownloading = false;
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          console.error('Error downloading document:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  isMarkedForRemoval(item: OdvDocuments): boolean {
    return !!item.id && this.markedForRemoval.has(item.id);
  }

  /** Marks or unmarks a file. Nothing leaves the server until the page saves. */
  toggleRemoval(item: OdvDocuments): void {
    if (!item.id) {
      return;
    }

    if (this.markedForRemoval.has(item.id)) {
      this.markedForRemoval.delete(item.id);
    } else {
      this.markedForRemoval.add(item.id);
    }

    this.pendingRemovalsChange.emit(this.markedForRemoval.size);
    this._cdr.markForCheck();
  }

  /**
   * Deletes the marked files. Called by the page when it saves, never on its own, so an
   * administrator can still change their mind after clicking "svuota".
   */
  applyPendingRemovals(): Observable<unknown> {
    const ids = [...this.markedForRemoval];
    if (ids.length === 0) {
      return of(null);
    }

    return forkJoin(ids.map((id) => this.odvDocumentsService.deleteOdvDocument(id))).pipe(
      tap(() => {
        this.markedForRemoval.clear();
        this.pendingRemovalsChange.emit(0);
        this.loadOdvHistory();
      })
    );
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  refreshHistory(): void {
    if (this.salesOrderDocumentId) {
      this.loadOdvHistory();
    }
  }
}
